import { Box, Button, CircularProgress, IconButton, Tooltip } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ConfirmModal from "../ItemProperties/Forms/FieldDataValidation/ConfirmModal";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useTranslation } from "react-i18next";
import { cloneDeep, isObject } from "lodash";
import CommonGridContextMenu from "./CommonGridContextMenu";
import { t } from "i18next";
import { useDispatch } from "react-redux";
import { api } from "../../redux/api";
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import LightTooltip from "../../commonComponents/LightTooltip";


// In this Object we keep those values which should be ignored while pasting data, 
// Values should be mapped to key which was given as a fieldName in columnDefs
const ignoreValues = {
  fieldType : ["SELECT",t("SELECT"),t("EMPTY_COMBO")]
}

const iconStyle = {
  cursor: "pointer",
  color: "#666",
};

const valueCellStyle = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "calc(100% - 10%)",
};

const CommonGrid = ({
  showContextMenu,
  containerStyle = {},
  containerClasses = "",
  containerProps = {},
  getGridAPI,
  rowData = [],
  gridOptions,
  gridProps = {},
  isLoading,
  fullSize = true,
  pasteValidator,
  delimeter,
  fieldDelimeter,
  enableAutoBlur = true,
  avoidMultipleRowsValidation = false,
  identifier,
  customDataToArray,
  contextMenuId,
  customLoadingClass
}) => {
  const containerRef = useRef();
  const gridRef = useRef();
  const [gridAPI, setGridAPI] = useState();
  const selectedCell = useRef();
  const delimiterRef = useRef();
  const fieldDelimiterRef = useRef();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const sizeToFit = (api) => {
    if (api && fullSize) {
      api.sizeColumnsToFit({
        defaultMinWidth: 100,
      });
    }
  };

  const handleResize = () => {
    sizeToFit(gridRef.current.api);
  };

  const gridReadyHandler = (params) => {
    if (params?.api) {
      if (getGridAPI) getGridAPI(params.api, gridRef.current);
      setGridAPI(params.api);
      if (identifier === "Quick Add") {
        dispatch(
          api.endpoints.getQuickAddColumnOrder.initiate(
            {},
            { forceRefetch: true }
          )
        ).then((response) => {
          const data = response?.data?.data;
          if (response && data && data.column_order.length > 0) {
             const savedColumnOrder = data.column_order;
            if (params.api)
              params.api.applyColumnState({
                state: savedColumnOrder,
                applyOrder: true,
              });
          }
          if(response.data.current_state === 'ERROR'){
            console.log('Error fetching column order')
          }
        });
      }
    }
  };

  const scrollToFocusedCell = ({ api }) => {
    const focusedCell = api.getFocusedCell();
    const rowIndex = focusedCell?.rowIndex;
    const columnId = focusedCell?.column?.colId;
    try {
      api?.ensureIndexVisible(rowIndex + 1);
      api?.scrollToColumn(columnId);
    } catch (err) {}
  };

  const CustomCellRenderer = (
    info,
    isDeleteControl,
    deleteHandlerCallBack = () => {},
    showDeleteIcon = () => true,
    showDeleteIconOnly,
    placeholder,
    showPlaceholder = true
  ) => {
    const { value, data } = info;
    const [openModal, setOpenModal] = useState(false);
    const [message, setMessage] = useState("");
    
    const handleClick = () => {
      const message = t("BOX_ARE_YOU_SURE_YOU_WANT_TO_DEL_")
        .split("{0}")
        .map((e, i) => {
          return i === 0 ? (
            <span key={i + Math.random() }>{e}</span> // Fixing Key Error
          ) : (
            <>
              {" "}
              <span key={Math.random() + i} style={{ fontWeight: "600" }}>
                {" "}
                {info?.data?.key || ""}{" "}
              </span>{" "}
              <span key={Math.random() + i}>{e}</span>{" "}
            </>
          );
        });
      setMessage(message);
      setOpenModal(true);
    };

    const deleteHandler = useCallback(
      (id, event) => {
        event.stopPropagation();
        const newRowData = [];
        gridRef.current.api.forEachNode((node) => {
          if (node?.data?.id !== id) {
            const newIndex = newRowData.length;
            newRowData.push({ ...node.data, id: newIndex });
          }
        });
        gridRef.current.api.setGridOption('rowData', newRowData)
      },
      [rowData]
    );

    return (
      <div
        style={{
          display: showDeleteIconOnly ? "block" : "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {(!showDeleteIconOnly && !value) || /^\s*$/.test(value) ? (
          <span style={{ color: "#0000008A" }}>{ showPlaceholder ? placeholder : ''}</span>
        ) : (
          <div style={valueCellStyle}>{value}</div>
        )}
        {isDeleteControl && showDeleteIcon(data) ? (
          // <DeleteOutlineOutlinedIcon onClick={handleClick} sx={iconStyle} />
          <LightTooltip
              title={t('DELETE')}
              arrow>
              <span style={{ cursor: 'pointer' }} className="DELETE_MUI" onClick={handleClick}></span>
            </LightTooltip>
        ) : (
          ""
        )}
        <ConfirmModal
          openModal={openModal}
          message={message}
          handleClose={setOpenModal}
          yesHandler={(e) => {
            deleteHandler(data.id, e);
            deleteHandlerCallBack(data);
          }}
          noHandler={() => setOpenModal(false)}
        />
      </div>
    );
  };

  const CustomColumnHeader = (props) => {
    
    const clickHandler = () => {
      if (props?.customClickHandlerOnAdd) {
        props?.customClickHandlerOnAdd(props)
        return;
      }
      // Get the total row count from the grid API
      const rowCount = props.api.getDisplayedRowCount();
      const lastRowIndex = rowCount > 0 ? rowCount - 1 : 0;
  
      if (rowCount > 0) {
        props.api.ensureIndexVisible(lastRowIndex);
        setTimeout(() => {
          props.api.ensureIndexVisible(lastRowIndex);
          setTimeout(() => {
            gridRef.current.api.startEditingCell({
              rowIndex: lastRowIndex,
              colKey: props.column.colId,
            });
          },100);
      }, 100);
        if (props?.addNewAfterHandler) props?.addNewAfterHandler(props, lastRowIndex);
      } 
    };
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <div>{props.displayName}</div>
        {props?.addNewControl ? 
          <LightTooltip style={{cursor: 'pointer'}} onClick={clickHandler} title={props?.addControlLabel || ''}>
            <AddOutlinedIcon/>
          </LightTooltip>
        : ''}
      </div>
    );
  }

  const addNewRow = (e) => {
    const node = e.api.getRowNode(e.rowIndex); // Gettin current rownode.
    const index = node?.data?.id; // Checking index of that (0 based indexing)
    const length = e?.api?.getDisplayedRowCount(); // Overall Length of rowData
    const lastNode = e?.api?.getRowNode(length - 2)?.data || {}; // Getting second last node
    const isNotEmpty = Object?.values(lastNode)?.filter((e) => {
      if(!isObject(e)) return e;
    }).length ; // Checking if it is empty or contains any data
    if (index >= 0 && ((index === 0 && length === 1) || (index === length - 1 && isNotEmpty))) {
      if (identifier === "Quick Add") {
        const tempNode = cloneDeep(e?.api?.getRowNode(length - 1)?.data);
        const propsToChk = ["req","quoteData","trimData","urlEncodeData","suppressFieldDelimiter"]
        propsToChk.map((prop) => {
          if(tempNode?.hasOwnProperty(prop)) delete tempNode[prop]
        })
        if (tempNode?.fieldType ==="SELECT") delete tempNode["fieldType"];
        if (tempNode?.hl7DataType ==="-- Select --") delete tempNode["hl7DataType"];
        const isNotEmpty = Object?.values(tempNode)?.filter((e) => {
          if(!isObject(e)) return e;
        })?.length;
        if (!isNotEmpty) return;
      } 
      else if(identifier === "trading_partner_keys"){
        const tempNode = cloneDeep(e?.api?.getRowNode(length - 1)?.data);
        const propsToChk = ["unique", "searchable"]
        propsToChk.map((prop) => {
          if(tempNode?.hasOwnProperty(prop)) delete tempNode[prop]
        })
       
        const isNotEmpty = Object?.values(tempNode)?.filter((e) => {
          if(!isObject(e)) return e;
        })?.length > 1;
        if (!isNotEmpty) return;
      }
      const newData = { id: length };
      const columns = gridOptions?.columnDefs?.map((e) => e.field) || [];
      columns?.forEach((e) => {
        if (e?.field) newData[e] = "";
      });
      e.api.applyTransaction({ add: [newData] });
      // e.api.refreshCells();
      scrollToFocusedCell(e);
    }
  };

  const dataToArray = (strData, delimeter, lineDelimeter) => {
    // const delimiter = '\t';

    // Regular expression pattern to match the delimiter and values in the string data
    // const objPattern = new RegExp(`(\\${delimiter}|\\r?\\n|\\r|^)(?:"([^"]*(?:""[^"]*)*)"|([^"\\"${delimiter}\\r\\n]*))`, 'gi');
    if (customDataToArray) return customDataToArray(strData);
    const cleanString = strData.replace(/\r/g, '')
    let arrData = cleanString.split(delimeter || '\n')?.map(row => row?.split(lineDelimeter || '\t')) || [];
    arrData = arrData.filter(e => {
      let isNotEmpty = false;
      e?.forEach( o => {
        if(o) isNotEmpty = true
      })
      return isNotEmpty;
    })
    return arrData;
  };

  const handleDocumentClick = (event) => {
    // If user click outside cell then it should Stop Editing Cell
    const isInsideCell = event.target.closest(".ag-cell");
    const isContextMenuClicked = event.target.closest(".ag-grid-context-menu");
    if (!isInsideCell && !isContextMenuClicked && gridRef?.current?.api && enableAutoBlur) {
      gridRef.current.api.stopEditing(); // Stop editing when clicking outside the grid
    }
  };

  const delimeterDecode = (val) => {
    if (!val) return val;
    if (val === "CRLF" || val === "%0D%0A") return `\n`;
    if (val === "CR" || val === "%0D" || val === "\r") return `\n`;
    if (val === "LF" || val === "%0A" || val === "\n") return `\n`;
    return decodeURIComponent(val);
  }

  const getCombinedData  = (oldData = [], newData = [], selectedCell = {}) => {
    let finalData = [];
    if ((selectedCell?.index + 1) <= oldData.length) {
      for (let i = 0; i < selectedCell.index || 0; i++){
        finalData.push(oldData[i]);
      }
      for (let i = 0; i < newData.length; i++){
        const newObj = {...(oldData?.[selectedCell.index + i] || {}), ...newData?.[i]};
        finalData.push(newObj);
      }
      for (let i = selectedCell?.index + newData.length; i < oldData.length; i++) {
        finalData.push(oldData[i]);
      }
    }else {
      finalData = [...oldData, ...newData];
    }
    return finalData;
  }

  const updateRowData = (api) => {
    if (avoidMultipleRowsValidation) return;
    let foundEmpty = false;
    const rowData = [];
    api.forEachNode( node => {
      const isEmpty = !Object?.keys(node.data)?.filter((e) => {
        if(!isObject(node?.data?.[e]) && e !== 'id') return node?.data?.[e];
      }).length 
      if (!foundEmpty) rowData.push(node.data);
      if (isEmpty) foundEmpty = true;
    })
    api.setGridOption('rowData', rowData)
  }

  const handlePasteEnd = async (event) => {
    event?.stopPropagation();
    if (!showContextMenu) return;
    
    // Check if the grid is currently in editing mode
    if (gridRef.current?.api?.getEditingCells()?.length > 0) {
      // If in editing mode, let the default paste behavior handle it
      return;
    }
    
    const pastedData = await navigator?.clipboard?.readText();
    const parsedData = dataToArray(pastedData, delimeterDecode(delimiterRef.current), delimeterDecode(fieldDelimiterRef.current));
    const columnState = gridRef.current.api.getColumnDefs();
    const currentRowStructure = {};
    const completeRowStructure = {};
    let found = false;
    columnState?.forEach(e => {
      if (selectedCell?.current?.cell === e?.field) found = true;
      if (e?.field && found) currentRowStructure[e?.field] = '';
      if (e?.field) completeRowStructure[e?.field] = '';
    })
    if (Object.keys(currentRowStructure)?.length){
      let newData = parsedData?.map( e => {
        const newRowData = {};
        Object.keys(currentRowStructure)?.forEach( (key, idx) => {
          newRowData[key] = e?.[idx] || '';
        })
        return newRowData;
      })
      let oldData = []
      gridRef?.current?.api?.forEachNode((node) => oldData.push(node?.data));
      oldData = oldData.filter( e => {
        let found = false;
        Object.keys(completeRowStructure)?.forEach( (key) => {
          if (e?.[key] && !ignoreValues?.[key]?.includes(e?.[key])) found = true;
        })
        return found;
      })
      if (pasteValidator) newData = pasteValidator(newData, parsedData, pastedData, selectedCell.current) || newData; // If there any validation needed while pasting then we can pass pasteValidator function
      const combinedDataArray = getCombinedData(oldData,newData,selectedCell.current);
      let combinedData = [...combinedDataArray, currentRowStructure];
      combinedData = combinedData.map((e, idx) => { return {...e, id: idx}})           // that will validate and return data accordingly
      gridRef.current.api.setGridOption('rowData', combinedData)
      gridRef.current.api.refreshCells();
      scrollToFocusedCell(gridRef.current);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleDocumentClick);
    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, []);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(handleResize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [isLoading]);

  useEffect(() => {
    document?.body?.addEventListener("paste",handlePasteEnd);
    return () => {
      document?.body?.removeEventListener("paste", handlePasteEnd);
    };
  }, [containerRef.current]);

  useEffect(() => {
    delimiterRef.current = delimeter;
    fieldDelimiterRef.current = fieldDelimeter
  },[delimeter, fieldDelimeter])



  let cleanGridOptions = {...(gridOptions || {})};
  if (gridOptions?.columnDefs) {
    cleanGridOptions.columnDefs = gridOptions.columnDefs.map((e) => {
      const newColumn = {
        width: e?.width ? e.width : e?.showDeleteIconOnly ? 70 : "",
        suppressSizeToFit: e?.suppressSizeToFit
          ? e.suppressSizeToFit
          : e?.showDeleteIconOnly
          ? true
          : false,
        cellRenderer: (info) =>
          CustomCellRenderer(
            info,
            e?.isDeleteControl,
            e?.deleteHandler,
            e?.showDeleteIcon,
            e?.showDeleteOnStart,
            e?.placeholder || e?.headerName,
            e?.showPlaceholder
          ),
        ...e,
      };

      if (newColumn.addNewControl) {
        newColumn.headerComponentParams = {
          addNewControl : true,
          addControlLabel: newColumn.addControlLabel,
          addNewAfterHandler: newColumn?.addNewAfterHandler,
          customClickHandlerOnAdd: newColumn?.customClickHandlerOnAdd
        }
      }

      // Removing any Custom Props for preventing Ag-grid Error
      const keys = Object.keys(newColumn);
      ['isDeleteControl', 'deleteHandler', 'showDeleteIcon', 'showDeleteOnStart','showPlaceholder',"showDeleteIconOnly", "addNewControl", "placeholder", "addControlLabel", "addNewAfterHandler", "customClickHandlerOnAdd"]
      .forEach( ele => {
        if(keys.includes(ele)) delete newColumn[ele] 
      })
      return newColumn;
    });
  }

  return (
    <>
      <Box
        id={contextMenuId ? contextMenuId : "ag-grid-target-id"}
        ref={containerRef}
        style={{
          visibility: isLoading ? "hidden" : "visible",
          position: "relative",
          height: containerStyle.height ? containerStyle.height : 400,
          ...containerStyle,
        }}
        className={`ag-theme-material table-wrapper ${containerClasses}`} // Give class to specify height (Important)
        {...containerProps}
      >
        <AgGridReact
          components={{agColumnHeader: CustomColumnHeader}}
          gridOptions={cleanGridOptions}
          rowData={rowData}
          onGridReady={gridReadyHandler}
          stopEditingWhenCellsLoseFocus={true}
          ref={gridRef}
          {...gridProps}
          onCellValueChanged={(e) => {
            if (gridProps?.onCellValueChanged) gridProps.onCellValueChanged(e);
            e.api.refreshCells({
              force: true, // Force re-rendering even if data hasn't changed
              suppressFlash: false, // Allow flashing effect on changed cells
            });
            if (!gridProps.disableNewRowOnCellValueChange) {
              addNewRow(e);
              updateRowData(e.api);
            }
          }}
          onCellClicked={(e) => {
            selectedCell.current = {cell : e?.column.colId, index : e?.rowIndex, data: e?.data}
            if(gridProps?.onCellClicked) gridProps.onCellClicked(e);
          }}
          onCellFocused={(e) => {
            const rowIndex = e.rowIndex;
            const displayedRow = e.api.getDisplayedRowAtIndex(rowIndex);
            const rowData = displayedRow ? displayedRow.data : null;
            if (gridProps.cellFocus) gridProps.cellFocus(e);
            selectedCell.current = {cell : e?.column.colId, index : e?.rowIndex, data: rowData}
            if(gridProps?.onCellFocused) gridProps.onCellFocused(e);
          }}
          onCellKeyDown={(e) => {
            selectedCell.current = {cell : e?.column.colId, index : e?.rowIndex, data: e?.data}
            if(gridProps?.onCellKeyDown) gridProps.onCellKeyDown(e);
          }}
          onRowDataUpdated={(e) => {
            sizeToFit(e.api);
            if (gridProps?.onRowDataUpdated) gridProps.onRowDataUpdated(e);
          }}
        />
      </Box>
      <CircularProgress
        sx={{
          margin: "auto",
          display: isLoading ? "block" : "none",
          position: "absolute",
          top: "40%",
          left: "45%",
        }}
        color="primary"
        className={customLoadingClass || ""}
      />
      {showContextMenu && <CommonGridContextMenu
        targetId={contextMenuId ? contextMenuId : "ag-grid-target-id"}
        pasteHandler={handlePasteEnd}
      />}
    </>
  );
};

export default CommonGrid;

/*
  This component returns a reusable AG-Grid component.

  Reusable Features:
  - AG-Grid auto-width resize (No extra props need to be passed for this).
  - Adding a new Row (Check addNewRow function for more details).
  - Deleting a Row

  Props for Configuring Delete Functionality (Example in the gridOptions Object of Account Data):
  - "isDeleteControl" (boolean): Mandatory if you want to show a delete icon in any cell.
  - "deleteHandler" (function): Callback function to run after deleting any row if needed. You can ignore it as per your need.
  - "showDeleteIcon" (function, optional): Function that returns a boolean to conditionally show the delete icon. Default is true.
  - "showDeleteIconOnly" (boolean, optional): Show only the delete icon in that column if true. Default is false. If false, both the value and delete icon will be shown in the column. 
    You can pass "width" but minimum width should be of 70 (that is default if you don't pass) in that columnDefs Object
  - "pasteValidator" (function): this function will validate data before pasting and return it

  Additional Information:
  - Id in rowData should be 0 based indexing numbers (Required in reIndexing of rows after delete and also new row).
  - Always pass a prop named "getGridAPI," which is a function that receives the grid API as its parameter. This is required to interact with the grid in your parent component.
  - Props such as "rowData," "gridOptions," "containerClasses," "isLoading," and "getGridAPI" can be passed directly. 
    Other props must be in object format. For AG-Grid props, use the "gridProps" object (key-value pairs), and for Grid Container props, use the "containerProps" object (key-value pairs).
  - If you want to modify the Delete Cell, you can do so in the "CustomCellRenderer" in the Grid Component.
  - If you want real time data then use gridAPI which comes from getGridAPI function.
  - showPlaceholder will show placeholder (Column Name) in Cells default value is true.
*/
